import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { useToastContext } from '../../../../../../contexts'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { emailRegex } from '../../../../../../utils/regex'
import { useConfigContext } from '../../../../../ui-config'
import { useAllSuppliers } from '../../../../suppliers/data'
import { RoleAssignmentDto, useAllRoleSets, useAllRoles } from '../../../data'
import {
  requestPostRoleAssignment,
  requestPostRoleAssignments,
} from '../../../data/api'
import { AddAssignee } from './AddAssignee'

interface FormValues {
  role?: string[]
  assignee?: string[]
  project?: string
}
export type FormFieldName = keyof FormValues

interface Props {
  setIsRequestRunning: (params: boolean) => void
  visible: boolean
  onHide: () => void
  mutate: () => void
  triggerStatus: (
    isCreateValue: boolean,
    isDeleteValue: boolean,
    location: string,
    retryAfter: string,
  ) => void
}

export const AddAssigneeContainer: React.FC<Props> = ({
  setIsRequestRunning,
  visible,
  onHide,
  mutate,
  triggerStatus,
}) => {
  const { t } = useTranslation()
  const { data: roleSets } = useAllRoleSets(true)
  const { data: suppliers } = useAllSuppliers()
  const { data: allRoles } = useAllRoles()

  const { uiConfig: config } = useConfigContext()
  const { showToast } = useToastContext()

  const projectRoles =
    allRoles?.filter(role => role.isProjectScope === true).map(r => r.name) ||
    []

  const tenantRoles =
    allRoles?.filter(role => role.isProjectScope !== true).map(r => r.name) ||
    []

  const [wrongEmails, setWrongEmails] = useState<string[] | null>(null)

  const validateEmailField = (
    emails: string[],
    fieldErrors: FormikErrors<FormValues>,
    fieldName: FormFieldName,
    setWrongEmails: Function,
  ) => {
    const emailValidationRegex = emailRegex
    const invalidEmails = emails?.filter(
      email => !emailValidationRegex.test(email),
    )
    if (invalidEmails?.length) {
      setWrongEmails(invalidEmails)
      fieldErrors[fieldName] = t(
        'settings.email.common.errorMessage.wrongEmail',
        { count: invalidEmails?.length },
      )
    } else {
      setWrongEmails(null)
    }
  }

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      role: [],
      assignee: [],
      project: undefined,
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      if (!data.assignee) errors.assignee = 'Assignee is required.'

      if (data.assignee)
        validateEmailField(data.assignee, errors, 'assignee', setWrongEmails)

      if (!data.role) errors.role = 'Role is required'

      data.role?.forEach(x => {
        if (projectRoles?.includes(x) && !data.project)
          errors.project = 'Project ist required for this role.'
      })

      return errors
    },
    onSubmit: async data => {
      try {
        onHide()
        setIsRequestRunning(true)

        formik.resetForm()
        data.role?.forEach(x => {
          if (!projectRoles.includes(x)) {
            data.project = ''
          }
        })
        await submitForm(data)
        if (data.assignee?.length === 1 && data.role?.length === 1) {
          showToast({
            style: {},
            closable: true,
            severity: 'success',
            summary: t(
              'roleAssignments.table.add.dialog.add.messages.success.summary',
            ),
            detail: t(
              'roleAssignments.table.add.dialog.add.messages.success.detail',
              {
                email: data.assignee,
                role: data.role,
              },
            ),
          })
        }
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'roleAssignments.table.add.dialog.add.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
        mutate()
      }
    },
  })

  const submitForm = async (formData: FormValues) => {
    if (formData.assignee?.length === 1 && formData.role?.length === 1) {
      const newData: RoleAssignmentDto = {
        assignee: formData.assignee[0],
        project: formData.project,
        role: formData.role[0],
      }
      await requestPostRoleAssignment(newData)
    } else {
      let newData: RoleAssignmentDto[] = []
      formData.assignee!.forEach(x => {
        let roleAssignment: RoleAssignmentDto
        formData.role!.forEach(y => {
          roleAssignment = {
            assignee: x.trim(),
            project: formData.project ? formData.project : '',
            role: y,
          }
          newData.push(roleAssignment)
        })
      })
      const result = await requestPostRoleAssignments(newData)

      triggerStatus(true, false, result.locationUrl, result.retry)
    }
  }

  const onHideDialog = () => {
    onHide()
    formik.resetForm()
  }

  const isFormFieldInvalid = (name: FormFieldName) =>
    !!(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return isFormFieldInvalid(name) ? (
      <small className="p-error">{formik.errors[name]}</small>
    ) : (
      <small className="p-error">&nbsp;</small>
    )
  }

  return (
    <AddAssignee
      formik={formik}
      isFormFieldInvalid={isFormFieldInvalid}
      getFormErrorMessage={getFormErrorMessage}
      config={config.selfservices.roleAssignments.createAssignee}
      roleSets={roleSets}
      wrongEmails={wrongEmails}
      suppliers={suppliers}
      projectRoles={projectRoles}
      tenantRoles={tenantRoles}
      visible={visible}
      onHide={onHideDialog}
    />
  )
}
